import {useSortable} from '@dnd-kit/sortable';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {isMobile} from '../../../util/panelbankFlow';
import {getPanelSpec} from '../../../util/panelTypes';
import {useFeatureFlagVegaEnabled} from '../../../util/useFeatureFlag';
import {getDndSortablePanelId} from '../../PanelBank/sectionLayout/utils';
import {useWorkspaceSectionContext} from '../../PanelBank/sectionLayout/WorkspaceSectionContextProvider';
import {PanelViewVegaSpec} from '../common/PanelViewVegaSpec';
import {PanelFullScreenButton} from '../FullScreenPanel/PanelFullScreenButton';
import {usePanelContext} from '../PanelContextProvider';
import {PanelEditButton} from '../PanelEditButton';
import {PanelRunMetricButton} from '../PanelRunMetricButton';
import {WorkspacePanelOverflowMenu} from './WorkspacePanelOverflowMenu';

export const WorkspacePanelHeader = () => {
  const {defaultMoveToSectionRef} = useWorkspaceSectionContext();
  const {isReadOnly, panelBankSectionConfigRef, panelRef, panel} =
    usePanelContext();

  const panelType = panel.viewType;
  const panelSpec = useMemo(() => getPanelSpec(panelType), [panelType]);

  const {entityName} = useProjectParams();
  const vegaPanelEnabled = useFeatureFlagVegaEnabled(entityName);

  const [isOverflowMenuActive, setIsOverflowMenuActive] = useState(false);

  const {setActivatorNodeRef, listeners, attributes} = useSortable({
    id: getDndSortablePanelId(panelRef),
  });

  return (
    <TailwindContents>
      <div
        className={twMerge(
          'relative flex shrink-0',
          'mb-4 min-h-[24px]',
          // on a regular workspace OR report where multiple panels are visible, we want to only show
          // the actionable left side items when the mouse is over the panel. it should always be visible in full screen where we only
          // show 1 panel
          'opacity-0 hover:opacity-100',
          // since we hide the actionable items until mouse hover on a regular workspace or report where we display multiple panels,
          // we still want to show the actionable items when overflow menu is active
          isOverflowMenuActive && 'opacity-100'
        )}
        data-test="panel-header">
        {!isReadOnly && (
          <Button
            ref={setActivatorNodeRef}
            {...listeners}
            {...attributes}
            aria-label="Drag panel"
            className="absolute left-0 top-0 m-0 cursor-grab active:cursor-grabbing"
            icon="drag-grip"
            size="small"
            variant="ghost"
          />
        )}
        <div
          className={twMerge(
            // on a regular workspace OR report where multiple panels are visible, the actions should be rendered on top
            // of the drag handle and there's no back button to consider
            'absolute right-8'
          )}>
          <div className={twMerge('flex')}>
            <PanelRunMetricButton
              entityName={entityName}
              panelBankSectionConfigRef={panelBankSectionConfigRef}
              panelRef={panelRef}
              panelType={panelType}
            />
            {isReadOnly && <PanelViewVegaSpec panelType={panelType} />}
            {!isMobile() && (
              <PanelFullScreenButton
                panelBankSectionConfigRef={panelBankSectionConfigRef}
              />
            )}
            {!isReadOnly &&
              !isMobile() &&
              !panelSpec.noEditMode &&
              (panelType !== 'Vega' || vegaPanelEnabled) && <PanelEditButton />}
            {!isReadOnly && panelBankSectionConfigRef != null && (
              <WorkspacePanelOverflowMenu
                defaultMoveToSectionRef={defaultMoveToSectionRef}
                panelSpec={panelSpec}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                isOpen={isOverflowMenuActive}
                setIsOpen={setIsOverflowMenuActive}
              />
            )}
          </div>
        </div>
      </div>
    </TailwindContents>
  );
};
